import React, { useState, useEffect } from 'react';
import {
  TextField,
  CircularProgress,
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  InputAdornment,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import GirviPageShell from '../common/GirviPageShell';
import DetailDialog, {
  DetailSection,
  DetailFieldGrid,
  DetailHeaderPhotos,
  money,
  listFilterFieldSx,
  listTableHeadSx,
  listActionBtnSx,
} from '../common/DetailDialog';
import PhotoThumb from '../common/PhotoThumb';
import baseURL from '../../baseURL';

const isManualReceipt = (receipt) => receipt?.kind === 'manual' || receipt?.source === 'manual';

const receiptClientPhoto = (receipt) =>
  receipt?.clientPhoto
  || receipt?.pledge?.clientPhoto
  || receipt?.ledger?.photo;

const receiptArticlePhoto = (receipt) =>
  receipt?.articlePhoto
  || receipt?.pledge?.articlePhoto;

const AllReceipts = () => {
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState([]);
  const [filteredReceipts, setFilteredReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [receiptToDelete, setReceiptToDelete] = useState(null);

  useEffect(() => {
    fetchReceipts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, dateFrom, dateTo, receipts]);

  const fetchReceipts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/receipts/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch receipts');
      }

      if (data.success) {
        setReceipts(data.data || []);
        setFilteredReceipts(data.data || []);  
        setSearchTerm('');
        setDateFrom('');
        setDateTo('');
      } else {
        setReceipts([]);
        setFilteredReceipts([]);
      }
    } catch (error) {
      console.error('Error fetching receipts:', error);
      setReceipts([]);
      setFilteredReceipts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    applyFilters();
  };

  const handleDateChange = (type) => (event) => {
    const date = event.target.value;
    if (type === 'from') {
      setDateFrom(date);
    } else {
      setDateTo(date);
    }
    applyFilters();
  };

  // Helper function to format date as dd/mm/yyyy
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const applyFilters = () => {
    let filtered = [...receipts];

    if (searchTerm) {
      const searchRegex = new RegExp(searchTerm, 'i');
      filtered = filtered.filter(receipt => 
        searchRegex.test(receipt.receiptNo || '') ||
        searchRegex.test(receipt.ticketNo || '') ||
        searchRegex.test(receipt.pledge?.loanDetails?.ticketNo || '') ||
        searchRegex.test(receipt.ledger?.firstName || '') ||
        searchRegex.test(receipt.ledger?.lastName || '') ||
        searchRegex.test(receipt.ledger?.code || '') ||
        searchRegex.test(receipt.description || '') ||
        searchRegex.test(String(receipt.totalAmount || receipt.amount || ''))
      );
    }

    if (dateFrom || dateTo) {
      const fromDate = dateFrom ? new Date(dateFrom) : null;
      const toDate = dateTo ? new Date(dateTo) : null;
      
      filtered = filtered.filter(receipt => {
        const receiptDate = new Date(receipt.date);
        if (fromDate && receiptDate < fromDate) return false;
        if (toDate && receiptDate > toDate) return false;
        return true;
      });
    }

    setFilteredReceipts(filtered);
  };

  const handleViewDetails = (receipt) => {
    setSelectedReceipt(receipt);
    setOpenDetails(true);
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
  };

  const handleEdit = (receipt) => {
    if (isManualReceipt(receipt)) {
      alert('Manual accounting receipts cannot be edited. Delete and create a new entry if needed.');
      return;
    }
    navigate(`/pledge/create-receipt?edit=${receipt._id}`);
  };

  const handleDelete = (receipt) => {
    setReceiptToDelete(receipt);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!receiptToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const url = isManualReceipt(receiptToDelete)
        ? `${baseURL}/accounting-entries/${receiptToDelete._id}`
        : `${baseURL}/receipts/${receiptToDelete._id}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && (data.success !== false)) {
        alert('Receipt deleted successfully. Accounting entries have been reversed.');
        fetchReceipts();
        setDeleteConfirmOpen(false);
        setReceiptToDelete(null);
      } else {
        alert(data.message || 'Failed to delete receipt');
      }
    } catch (error) {
      console.error('Error deleting receipt:', error);
      alert('Error deleting receipt: ' + error.message);
    }
  };

  const handlePrint = async (receipt) => {
    if (isManualReceipt(receipt)) {
      alert('Print is not available for accounting-tab manual receipts.');
      return;
    }
    try {
      // Import print utility
      const { printReceipt } = await import('../../utils/printUtility');
      
      // Prepare receipt data for printing
      const receiptData = {
        ...receipt,
        customer: receipt.ledger ? {
          firstName: receipt.ledger.firstName || '',
          lastName: receipt.ledger.lastName || '',
          code: receipt.ledger.code || '',
          address: receipt.ledger.address || '',
          mobileNo: receipt.ledger.mobileNo || ''
        } : null,
        pledgeData: receipt.pledge || null
      };
      
      await printReceipt(receiptData);
    } catch (error) {
      console.error('Error printing receipt:', error);
      alert('Error printing receipt. Please try again.');
    }
  };

  return (
    <>
      <GirviPageShell
        title="All Receipts"
        subtitle="Pledge · Receipts"
        chipLabel="Pledge"
        maxWidth="xl"
      >
        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            p: { xs: 1.5, md: 2 },
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            bgcolor: '#f8fafc',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Receipt no, ticket, customer, amount"
                sx={listFilterFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Date From"
                type="date"
                value={dateFrom}
                onChange={handleDateChange('from')}
                InputLabelProps={{ shrink: true }}
                sx={listFilterFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Date To"
                type="date"
                value={dateTo}
                onChange={handleDateChange('to')}
                InputLabelProps={{ shrink: true }}
                sx={listFilterFieldSx}
              />
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              overflow: 'auto',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={listTableHeadSx}>Date</TableCell>
                  <TableCell sx={listTableHeadSx}>Receipt</TableCell>
                  <TableCell sx={listTableHeadSx}>Ticket</TableCell>
                  <TableCell sx={listTableHeadSx}>Photos</TableCell>
                  <TableCell sx={listTableHeadSx}>Customer</TableCell>
                  <TableCell sx={listTableHeadSx} align="right">Interest</TableCell>
                  <TableCell sx={listTableHeadSx} align="right">Total</TableCell>
                  <TableCell sx={{ ...listTableHeadSx, cursor: 'default' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReceipts.length > 0 ? (
                  filteredReceipts.map((receipt) => {
                    const manual = isManualReceipt(receipt);
                    const ticketLabel = manual
                      ? 'Accounting'
                      : (receipt.ticketNo || receipt.pledge?.loanDetails?.ticketNo || 'N/A');
                    return (
                      <TableRow
                        key={receipt._id}
                        hover
                        sx={{ '&:last-child td': { borderBottom: 0 } }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap', color: '#475569', fontWeight: 600 }}>
                          {formatDate(receipt.date)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={receipt.receiptNo || receipt.entryNo || 'N/A'}
                            sx={{
                              fontWeight: 800,
                              bgcolor: 'rgba(201,162,77,0.14)',
                              color: '#8a6d2f',
                              border: '1px solid rgba(201,162,77,0.35)',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {ticketLabel}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <PhotoThumb src={receiptClientPhoto(receipt)} label="Client" size={36} />
                            <PhotoThumb src={receiptArticlePhoto(receipt)} label="Article" size={36} />
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {receipt.ledger
                            ? `${receipt.ledger.firstName || ''} ${receipt.ledger.lastName || ''}`.trim() || 'N/A'
                            : 'N/A'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {manual
                            ? '—'
                            : money(receipt.interestDetails?.calculatedInterest)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap' }}>
                          {money(receipt.totalAmount || receipt.amount)}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="View">
                              <IconButton onClick={() => handleViewDetails(receipt)} sx={listActionBtnSx} size="small">
                                <VisibilityIcon sx={{ fontSize: 18, color: '#1e3a5f' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={manual ? 'Manual receipts cannot be edited' : 'Edit'}>
                              <span>
                                <IconButton
                                  onClick={() => handleEdit(receipt)}
                                  sx={listActionBtnSx}
                                  size="small"
                                  disabled={manual}
                                >
                                  <EditIcon sx={{ fontSize: 18, color: manual ? '#94a3b8' : '#15803d' }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton onClick={() => handleDelete(receipt)} sx={listActionBtnSx} size="small">
                                <DeleteIcon sx={{ fontSize: 18, color: '#b91c1c' }} />
                              </IconButton>
                            </Tooltip>
                            {!manual && (
                              <Tooltip title="Print">
                                <IconButton onClick={() => handlePrint(receipt)} sx={listActionBtnSx} size="small">
                                  <PrintIcon sx={{ fontSize: 18, color: '#0369a1' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5, color: '#94a3b8' }}>
                      No receipts found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </GirviPageShell>

      <DetailDialog
        open={openDetails}
        onClose={handleCloseDetails}
        title={`Receipt ${selectedReceipt?.receiptNo || selectedReceipt?.entryNo || '—'}`}
        subtitle="Receipt details"
        headerPhotos={
          <DetailHeaderPhotos
            clientPhoto={receiptClientPhoto(selectedReceipt)}
            articlePhoto={receiptArticlePhoto(selectedReceipt)}
          />
        }
      >
        {selectedReceipt && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <DetailSection title="Receipt info">
                  <DetailFieldGrid
                    items={[
                      { label: 'Receipt No', value: selectedReceipt.receiptNo || selectedReceipt.entryNo || 'N/A' },
                      { label: 'Date', value: formatDate(selectedReceipt.date) },
                      {
                        label: 'Ticket No',
                        value: isManualReceipt(selectedReceipt)
                          ? 'Accounting'
                          : (selectedReceipt.ticketNo || selectedReceipt.pledge?.loanDetails?.ticketNo || 'N/A'),
                      },
                      { label: 'Transaction Type', value: selectedReceipt.transactionType || 'N/A' },
                    ]}
                  />
                </DetailSection>
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailSection title="Customer">
                  <DetailFieldGrid
                    items={[
                      { label: 'Customer Code', value: selectedReceipt.ledger?.code || 'N/A' },
                      {
                        label: 'Name',
                        value: `${selectedReceipt.ledger?.firstName || 'N/A'} ${selectedReceipt.ledger?.lastName || ''}`.trim(),
                      },
                    ]}
                  />
                </DetailSection>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <DetailSection title="Interest">
                  <DetailFieldGrid
                    items={[
                      { label: 'Interest Type', value: selectedReceipt.interestDetails?.type || 'N/A' },
                      { label: 'Months', value: selectedReceipt.interestDetails?.months || 'N/A' },
                      { label: 'Interest', value: money(selectedReceipt.interestDetails?.calculatedInterest) },
                      { label: 'Total Amount', value: money(selectedReceipt.totalAmount || selectedReceipt.amount) },
                    ]}
                  />
                </DetailSection>
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailSection title="Adjustments">
                  <DetailFieldGrid
                    items={[
                      { label: 'Principal Adjustment', value: money(selectedReceipt.adjustments?.principalAdjustment) },
                      { label: 'Interest Adjustment', value: money(selectedReceipt.adjustments?.interestAdjustment) },
                      { label: 'Total Amount', value: money(selectedReceipt.totalAmount || selectedReceipt.amount) },
                    ]}
                  />
                </DetailSection>
              </Grid>
            </Grid>
          </>
        )}
      </DetailDialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this receipt? This action cannot be undone.
            {receiptToDelete && (
              <Box sx={{ mt: 1.5 }}>
                <strong>Receipt No:</strong> {receiptToDelete.receiptNo || receiptToDelete.entryNo || 'N/A'}<br />
                <strong>Ticket No:</strong> {receiptToDelete.ticketNo || receiptToDelete.pledge?.loanDetails?.ticketNo || 'N/A'}<br />
                <strong>Amount:</strong> {money(receiptToDelete.totalAmount || receiptToDelete.amount)}
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AllReceipts;
